import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useSessionStore } from "../store/useSessionStore";
import { useStore } from "../store/globalState";
import { setWidgetLinkId } from "../services/api";
import { resolveUrlLinkId } from "../utils/resolveUrlLinkId";
import {
  resetAnalytics,
  maybeReportAnalyticsSession,
  wasAnalyticsHandoffConsumed,
} from "../analytics";

/**
 * Runs once at the app root. Two jobs:
 *  1. If the URL link differs from the persisted (browser storage) session,
 *     purge the stale session BEFORE any page reads it, so a brand-new link
 *     never shows the previous link's data.
 *  2. Restore the in-memory widget link id + global linkId after refresh / HMR.
 */
export function useSessionBootstrap() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const storedLinkId = useSessionStore((state) => state.session.data?.linkId);
  const resetSession = useSessionStore((state) => state.resetSession);
  const setLinkId = useStore((state) => state.setLinkId);

  useEffect(() => {
    const urlLinkId = resolveUrlLinkId(location.pathname, searchParams);

    // New link in the URL but storage still holds a different one → wipe stale
    // data and let useClaimLink claim the new link from scratch.
    if (urlLinkId && storedLinkId && urlLinkId !== storedLinkId) {
      // Cold link change only — do not reset after a cross-host PostHog handoff.
      if (!wasAnalyticsHandoffConsumed()) {
        resetAnalytics();
      }
      useStore.getState().resetComposeState();
      resetSession();
      setWidgetLinkId(null);
      setLinkId(null);
      return;
    }

    // Same link (or none in URL): rewire API access from the persisted session.
    if (storedLinkId) {
      setWidgetLinkId(storedLinkId);
      setLinkId(storedLinkId);
      if (wasAnalyticsHandoffConsumed()) {
        maybeReportAnalyticsSession(storedLinkId);
      }
    }
  }, [
    location.pathname,
    searchParams,
    storedLinkId,
    resetSession,
    setLinkId,
  ]);
}
